import React, { useCallback, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Button,
  Icon,
  FormInputText,
  FormInputNumber,
  P,
} from '@fashionnovateam/nova-ui-toolkit';

interface FiberContent {
  id: string;
  [key: string]: string | number;
}

interface Props {
  fieldPath?: string;
  fieldNames?: {
    fiberField: string;
    percentageField: string;
  };
}

export function FiberContentDataGrid({
  fieldPath = 'fiberContents',
  fieldNames = { fiberField: 'fiber', percentageField: 'percent_of_fibers' },
}: Props) {
  const {
    watch,
    setValue,
    getValues,
    formState,
    control,
    setError,
    clearErrors,
  } = useFormContext();

  const fiberContents: FiberContent[] = watch(fieldPath) || [];

  const totalPercentage = fiberContents.reduce(
    (sum: number, fiber) =>
      sum + (Number(fiber[fieldNames.percentageField]) || 0),
    0,
  );

  const hasFilledValues = fiberContents.some(
    (fiber) =>
      (fiber[fieldNames.fiberField] &&
        String(fiber[fieldNames.fiberField]).trim() !== '') ||
      (fiber[fieldNames.percentageField] != null &&
        Number(fiber[fieldNames.percentageField]) > 0),
  );
  const hasPercentageError =
    (formState.isSubmitted ||
      totalPercentage > 100 ||
      (hasFilledValues && totalPercentage !== 100)) &&
    totalPercentage !== 100;

  const validateTotalPercentage = useCallback(() => {
    if (hasFilledValues && totalPercentage !== 100) {
      return 'Fiber percentages must equal 100%';
    }
    return true;
  }, [hasFilledValues, totalPercentage]);

  useEffect(() => {
    const validationResult = validateTotalPercentage();

    if (validationResult !== true) {
      setError(fieldPath, {
        type: 'manual',
        message: validationResult,
      });
    } else {
      clearErrors(fieldPath);
    }
  }, [validateTotalPercentage, fieldPath, setError, clearErrors]);

  const addFiber = useCallback(() => {
    const currentContents: FiberContent[] = getValues(fieldPath) || [];
    const newFiber: FiberContent = {
      id: `fiber-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      [fieldNames.fiberField]: '',
      [fieldNames.percentageField]: 0,
    };
    setValue(fieldPath, [...currentContents, newFiber]);
  }, [getValues, setValue, fieldPath, fieldNames]);

  const removeFiber = useCallback(
    (id: string) => {
      const currentContents: FiberContent[] = getValues(fieldPath) || [];
      setValue(
        fieldPath,
        currentContents.filter((fiber) => fiber.id !== id),
      );
    },
    [getValues, setValue, fieldPath],
  );

  return (
    <div className="space-y-3">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-neutral-100 border-b border-gray-200 grid grid-cols-[1fr_2fr_2fr_60px] gap-4 px-4 py-3">
          <P color="disabled" size="xs" weight="medium">
            Fiber
          </P>
          <P color="disabled" size="xs" weight="medium">
            Fiber Type
          </P>
          <P color="disabled" size="xs" weight="medium">
            Percent of Fibers (%)
          </P>
          <div />
        </div>

        <div className="divide-y divide-gray-200">
          {fiberContents.map((fiber, index: number) => (
            <div
              key={fiber.id}
              className="grid grid-cols-[1fr_2fr_2fr_60px] gap-4 px-4 py-3 items-start"
            >
              <P size="sm" className="pt-4">
                Fiber {index + 1}
              </P>

              <div>
                <Controller
                  name={`${fieldPath}.${index}.${fieldNames.fiberField}`}
                  control={control}
                  rules={{
                    required: 'Fiber type is required',
                    minLength: {
                      value: 1,
                      message: 'Fiber type cannot be empty',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <FormInputText
                      label=""
                      value={String(field.value || '')}
                      onChangeValue={field.onChange}
                      placeholder="ie. Cotton"
                      error={error?.message}
                      className="w-full"
                    />
                  )}
                />
              </div>

              <div>
                <Controller
                  name={`${fieldPath}.${index}.${fieldNames.percentageField}`}
                  control={control}
                  rules={{
                    required: 'Percentage is required',
                    min: {
                      value: 0.01,
                      message: 'Percentage must be greater than 0',
                    },
                    max: {
                      value: 100,
                      message: 'Percentage cannot exceed 100',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <FormInputNumber
                      label=""
                      value={
                        Number(field.value) === 0
                          ? undefined
                          : Number(field.value)
                      }
                      onChangeValue={(val: number | undefined) =>
                        field.onChange(val === undefined ? 0 : val)
                      }
                      placeholder="1"
                      min="1"
                      step="1"
                      max="100"
                      endAdornment={
                        <P color="disabled" size="sm">
                          %
                        </P>
                      }
                      error={error?.message}
                      className="w-full"
                    />
                  )}
                />
              </div>

              <div className="flex justify-center pt-3">
                {fiberContents.length > 1 && (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => removeFiber(fiber.id)}
                    className="p-1"
                  >
                    <Icon name="delete/delete-dustbin-02" size="16px" />
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="bg-neutral-100 grid grid-cols-[1fr_2fr_2fr_60px] gap-4 px-4 py-3 items-center">
            <P size="xs" weight="medium">
              Total
            </P>
            <P size="xs" color="secondary">
              -
            </P>
            <P size="xs" weight="medium">
              {totalPercentage}%
            </P>
            <div />
          </div>
        </div>
      </div>

      {hasPercentageError && (
        <div className="flex items-center text-danger-700">
          <Icon
            name="alert/alert-circle"
            variant="stroke"
            size="12px"
            color="red"
          />
          <P size="xs" color="error" weight="medium" className="ml-1">
            Percent of fibers must equal 100%.
          </P>
        </div>
      )}

      <Button
        onClick={addFiber}
        color="secondary"
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
      >
        <Icon name="plus/plus-circle" variant="solid" size="16px" />
        Add Fiber
      </Button>
    </div>
  );
}
